import React, { CSSProperties, FunctionComponent, HTMLAttributes, ReactElement, ReactNode } from 'react';
import cn from '../cn';

// --- FormControl (MUI's fieldset-ish wrapper → div) ---
type FormControlProps = { fullWidth?: boolean; children?: ReactNode } & Omit<HTMLAttributes<HTMLDivElement>, 'color'>;
export const FormControl = React.forwardRef<HTMLDivElement, FormControlProps>(
  ({ fullWidth = false, className = '', children, ...rest }, ref) => (
    <div ref={ref} className={cn('inline-flex flex-col', fullWidth && 'w-full', className)} {...rest}>{children}</div>
  ),
);
FormControl.displayName = 'FormControl';

// --- FormControlLabel (control + label) ---
type FormControlLabelProps = {
  control: ReactElement,
  label: ReactNode,
  value?: string,
  checked?: boolean,
  onChange?: (e: any) => void,
  name?: string,
  disabled?: boolean,
  className?: string,
};
export const FormControlLabel: FunctionComponent<FormControlLabelProps> = ({ control, label, checked, onChange, name, disabled, className = '' }) => {
  // Inject group-provided checked/onChange/name only when the control doesn't set its own
  const injected: any = {};
  if (checked !== undefined && control.props.checked === undefined) injected.checked = checked;
  if (onChange !== undefined && control.props.onChange === undefined) injected.onChange = onChange;
  if (name !== undefined && control.props.name === undefined) injected.name = name;
  return (
    <label className={cn('ds-control-row select-none', disabled && 'cursor-default opacity-50', className)}>
      {Object.keys(injected).length ? React.cloneElement(control, injected) : control}
      <span>{label}</span>
    </label>
  );
};

// --- Radio ---
type RadioProps = {
  checked?: boolean,
  onChange?: (e: any) => void,
  value?: string,
  name?: string,
  disabled?: boolean,
  style?: CSSProperties,
  className?: string,
};
export const Radio: FunctionComponent<RadioProps> = ({ checked, onChange, value, name, disabled, className = '', style }) => (
  <input
    type="radio"
    checked={checked}
    onChange={onChange}
    value={value}
    name={name}
    disabled={disabled}
    style={style}
    className={cn('ds-radio', className)}
  />
);

// --- RadioGroup (clones FormControlLabel children, wiring checked/onChange by value) ---
type RadioGroupProps = {
  value?: string,
  onChange?: (event: any, value: string) => void,
  row?: boolean,
  name?: string,
  children?: ReactNode,
  className?: string,
};
export const RadioGroup: FunctionComponent<RadioGroupProps> = ({ value, onChange, row = false, name, children, className = '' }) => (
  <div role="radiogroup" className={cn('flex', row ? 'flex-row gap-4' : 'flex-col', className)}>
    {React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) return child;
      const childValue = (child.props as any).value;
      return React.cloneElement(child as ReactElement<any>, {
        checked: value === childValue,
        name,
        onChange: (e: any) => onChange?.(e ?? { target: { value: childValue } }, childValue),
      });
    })}
  </div>
);
